import * as FileSystem from 'expo-file-system';
import { useState, useCallback } from 'react';
import { Platform, Share } from 'react-native';

type DownloadOptions = {
  fileName?: string;
  mimeType?: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
};

export const useDownloadFileWithScreenLock = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const openFileAnotherTab = useCallback((urlOrigin: string) => {
    window.open(urlOrigin, '_blank', 'noopener,noreferrer');
  }, []);

  const downloadFile = useCallback(async (urlOrBase64: string, options: DownloadOptions = {}) => {
    const {
      fileName = 'download',
      mimeType = 'application/octet-stream',
      onSuccess,
      onError,
    } = options;

    setIsLoading(true);
    setError(null);

    try {
      if (Platform.OS === 'web') {
        // Handle web download
        let blob: Blob;

        if (urlOrBase64.startsWith('data:')) {
          // Handle base64 data URL
          const response = await fetch(urlOrBase64);
          blob = await response.blob();
        } else if (urlOrBase64.startsWith('http')) {
          // Handle remote URL
          const response = await fetch(urlOrBase64);
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          blob = await response.blob();
        } else {
          // Assume it's a base64 string
          const byteCharacters = atob(urlOrBase64);
          const byteNumbers = new Array(byteCharacters.length);

          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }

          const byteArray = new Uint8Array(byteNumbers);
          blob = new Blob([byteArray], { type: mimeType });
        }

        // Create download link and trigger download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        // Handle React Native download
        if (urlOrBase64.startsWith('data:')) {
          // Handle base64 data URL
          const base64Data = urlOrBase64.split(',')[1];
          const fileUri = `${FileSystem.cacheDirectory}${fileName}`;

          await FileSystem.writeAsStringAsync(fileUri, base64Data, {
            encoding: FileSystem.EncodingType.Base64,
          });

          await Share.share({
            url: fileUri,
            title: `Download ${fileName}`,
          });
        } else if (urlOrBase64.startsWith('http')) {
          // Handle remote URL
          const downloadResumable = FileSystem.createDownloadResumable(
            urlOrBase64,
            `${FileSystem.cacheDirectory}${fileName}`,
            {}
          );

          const result = await downloadResumable.downloadAsync();

          if (!result) {
            throw new Error('Failed to download file');
          }

          await Share.share({
            url: result.uri,
            title: `Download ${fileName}`,
          });
        } else {
          // Handle base64 string
          const fileUri = `${FileSystem.cacheDirectory}${fileName}`;

          await FileSystem.writeAsStringAsync(fileUri, urlOrBase64, {
            encoding: FileSystem.EncodingType.Base64,
          });

          await Share.share({
            url: fileUri,
            title: `Download ${fileName}`,
          });
        }
      }

      onSuccess?.();
    } catch (err) {
      console.error('Download failed:', err);

      const localError = err instanceof Error ? err : new Error('Failed to download file');

      setError(localError);
      onError?.(localError);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { downloadFile, openFileAnotherTab, isLoading, error };
};
